/**
 * 基于window.postMessage实现的跨域页面通信机制
 * 可以正常跨域通信的情况：
 * 1.iframe加载跨域页面
 * 2.window.open打开新窗口
 * @author luoying<luoying@szzbmy.com>
 * @since 17/10/22
 */
import { isString, isFunction } from './check-types';
const readyType = '__POST_MESSAGER_READY__';
/**
 * 基于window.postMessage实现的跨域页面通信机制
 */
export default class PostMessager {
    constructor(win, force = false) {
        // 消息侦听器列表，按消息类型建立key-value索引
        this.messageListeners = new Map();
        // 等待发送的消息集合
        // 当通信机制未建立完毕之前，发送的消息会堆积起来
        // 当通信机制建立完毕之后，一次性全部发送出去
        this.waitingSenders = [];
        this.messageAllListeners = null;
        this.onReceiveMessage = (evt) => {
            // 非目标窗口的消息，过滤
            if (evt.source !== this.targetWindow) {
                return;
            }
            // 非规范消息，过滤
            if (!evt.data || !isString(evt.data)) {
                return;
            }
            try {
                // 解析消息体
                const res = JSON.parse(evt.data);
                // 接收到目标窗口发送来的准备就绪系统消息，表示通信机制已成功建立
                if (res.type === readyType) {
                    this.isReady = true;
                    // 一次性发送全部等待中的消息
                    for (const sender of this.waitingSenders) {
                        this.send(sender.type, sender.data);
                    }
                }
                // 若订阅了全局message,则会把得到的res全部推送出去
                if (this.messageAllListeners && isFunction(this.messageAllListeners)) {
                    this.messageAllListeners(res.data || res);
                }
                // 业务消息处理
                const listeners = this.messageListeners.get(res.type);
                if (!listeners || !listeners.length) {
                    return;
                }
                for (const listener of listeners) {
                    if (isFunction(listener)) {
                        listener(res.data || res);
                    }
                }
            }
            catch (err) {
                console.log(err);
            }
        };
        this.isReady = false;
        // 目标跨域窗口句柄
        this.targetWindow = win;
        // 是否为强制模式
        this.force = force;
        if (!this.targetWindow)
            return;
        window.addEventListener('message', this.onReceiveMessage, false);
        // 发送一条准备就绪的系统消息，通知目标窗口
        this.targetWindow.postMessage(this.createMessage(readyType), '*');
    }
    createMessage(type, data) {
        const msg = { type, data };
        return JSON.stringify(msg);
    }
    /**
     * 发送消息
     * @param  {string} type 消息类型
     * @param  {T} [data]    消息体（任意数据类型）
     * @return {boolean}
     */
    send(type, data) {
        if (!type || !isString(type) || !this.targetWindow) {
            return false;
        }
        // 通信机制未准备好，压入等待队列
        if (!this.isReady && !this.force) {
            const msg = { type, data };
            this.waitingSenders.push(msg);
            return false;
        }
        // 向目标窗口发送消息
        this.targetWindow.postMessage(this.createMessage(type, data), '*');
        return true;
    }
    listenAll(listener) {
        if (listener) {
            this.messageAllListeners = listener;
            return true;
        }
        return false;
    }
    /**
     * 侦听来自跨域页面的消息
     * @param {string} type 消息类型
     * @param {MessageListener} listener 侦听回调函数
     * @returns {boolean}
     */
    listen(type, listener) {
        if (!type || !listener || !isString(type) || !isFunction(listener)) {
            return false;
        }
        let listeners = this.messageListeners.get(type);
        if (!listeners) {
            listeners = [];
            this.messageListeners.set(type, listeners);
        }
        // 添加新的侦听器
        // 不允许重复添加同一个侦听器
        if (listeners.indexOf(listener) === -1) {
            listeners.push(listener);
        }
        return true;
    }
    /**
     * 取消消息侦听
     * @param {string} type 消息类型
     * @param {MessageListener} [listener] 侦听回调函数，当不传递此参数，则取消所有相同消息类型的侦听
     * @returns {boolean}
     */
    unlisten(type, listener) {
        if (!this.hasListen(type)) {
            return false;
        }
        if (!listener) {
            this.messageListeners.set(type, []);
            return true;
        }
        const listeners = this.messageListeners.get(type);
        if (listeners) {
            const idx = listeners.indexOf(listener);
            idx >= 0 && listeners.splice(idx, 1);
        }
        return true;
    }
    /**
     * 取消所有消息侦听
     */
    unlistenAll() {
        this.messageAllListeners = null;
    }
    /**
     * 检测是否已侦听了该类型的消息
     * @param  {string}  type 消息类型
     * @return {boolean}
     */
    hasListen(type) {
        if (!type || !isString(type)) {
            return false;
        }
        const listeners = this.messageListeners.get(type);
        return listeners && listeners.length > 1;
    }
    /**
     * 关闭跨域通信机制
     * 1.注销message事件
     * 2.取消所有侦听
     * 3.删除目标窗口句柄
     * 4.还原isReady
     */
    close() {
        window.removeEventListener('message', this.onReceiveMessage);
        this.unlistenAll();
        this.targetWindow = null;
        this.isReady = false;
        this.force = false;
    }
}
//# sourceMappingURL=post-messager.js.map